import {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useState,
  Dispatch,
  SetStateAction,
} from "react";
import {
  fetchUserAccountManagers,
  fetchUserOrganizations,
} from "@/modules/common/api/index.v2";
import { useQuery } from "@tanstack/react-query";
import useAuth from "@/modules/common/hooks/auth.hook";
import { queryCommonFlags } from "@/api/constants";
import {
  managedOrganization,
  Organization,
  UserTag,
} from "@/modules/common/dtos/index.v2";
import { Country, ORGANIZATION_TIER_TYPES } from "@/modules/common/constants";
import { EnvironmentVariables } from "@/modules/common/types";
import useLocalStorageState from "@/modules/common/hooks/local-storage.hook";
import { FilterType } from "@/modules/common/constants";
const PAGE_SIZE_INCREMENT = 50;
interface OrganizationContextType {
  renderOrganisationsActions: boolean;
  selectedOrganization?: Organization | undefined;
  /**
   * Operating base of the selected organization's tier, which decides whose inventory (areas,
   * developers, compounds) this user should see. Undefined until the organization has loaded —
   * callers should hold their inventory requests until then rather than guessing.
   */
  selectedOrganizationCountry?: Country;
  setSelectedOrganization: (organization: Organization | undefined) => void;
  selectedOrganizations: Organization[];
  setSelectedOrganizations: (organizations: Organization[] | undefined) => void;
  mainOrganization?: Organization;
  managedOrganizations?: managedOrganization[];
  filteredManagedOrganizations?: managedOrganization[];
  userTags?: UserTag[];
  userOrganizationsLoading: boolean;
  userTiers?: (typeof ORGANIZATION_TIER_TYPES)[keyof typeof ORGANIZATION_TIER_TYPES][];
  managedTaskeenOrganizations?: managedOrganization[];
  selectedSingleOrganization?: Organization;
  setSelectedSingleOrganization: (
    organization: Organization | undefined,
  ) => void;
  showSingleOrganizationTables: boolean;
  setShowSingleOrganizationTables: (show: boolean) => void;
  selectedManagers: any[];
  setSelectedManagers: React.Dispatch<React.SetStateAction<any[]>>;
  accountManagers: any;
  accountManagersLoading: boolean;
  accountManagersError: any;
  // fetchNextAccountManagers: () => void;
  // isFetchingNextAccountManagers: boolean;
  setAccountManagersPageSize: Dispatch<SetStateAction<number>>;
  setOrganizationsPageSize: Dispatch<SetStateAction<number>>;
  userOrganizationsByManagers: any;
  userOrganizationsByManagersLoading: boolean;
  userOrganizationsByManagersError: any;
  filterType: FilterType;
  setFilterType: Dispatch<SetStateAction<FilterType>>;
  showMultipleOrganizationTables: boolean;
  setShowMultipleOrganizationTables: Dispatch<SetStateAction<boolean>>;
  // fetchNextUserOrganizationsByManagers: () => void;
  // isFetchingNextUserOrganizationsByManagers: boolean;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(
  undefined,
);

export const OrganizationProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [selectedOrganization, setSelectedOrganization] = useState<
    Organization | undefined
  >(undefined);
  const [selectedSingleOrganization, setSelectedSingleOrganization] =
    useLocalStorageState(`selectedSingleOrganization`, {});
  const [selectedOrganizations, setSelectedOrganizations] =
    useLocalStorageState(`selectedOrganizations`, []);

  const [showSingleOrganizationTables, setShowSingleOrganizationTables] =
    useLocalStorageState(`showSingleOrganizationTables`, false);
  const [showMultipleOrganizationTables, setShowMultipleOrganizationTables] =
    useLocalStorageState(`showMultipleOrganizationTables`, false);
  const [filteredManagedOrganizations, setFilteredManagedOrganizations] =
    useState<managedOrganization[]>([]);
  const [allOrganizations, setAllOrganizations] = useState<
    managedOrganization[]
  >([]);

  const [selectedManagers, setSelectedManagers] = useState<any[]>([]);

  const [organizationsPageSize, setOrganizationsPageSize] =
    useState(PAGE_SIZE_INCREMENT);

  const [accountManagersPageSize, setAccountManagersPageSize] =
    useState(PAGE_SIZE_INCREMENT);
  const [filterType, setFilterType] = useState<FilterType>(FilterType.NONE);

  const {
    data: accountManagers,
    isLoading,
    // isFetchingNextPage,
    isError,
    // fetchNextPage,
  } = useQuery({
    queryKey: [
      "account-managers",
      accountManagersPageSize,
      selectedOrganizations,
    ],
    queryFn: () =>
      fetchUserAccountManagers({
        isManager: true,
        includeTags: ["9947c54f-0e0b-4bea-9c7e-dab104117cd7"],
        page: 1,
        pageSize: 50,
        manageOrganizationUIds: selectedOrganizations?.map(
          (org: Organization) => org?.uid,
        ),
      }),
    ...queryCommonFlags,
    staleTime: Infinity,
    keepPreviousData: true,
    enabled:
      !!user?.["cognito:username"] && filteredManagedOrganizations?.length > 0,
  });

  const { data: userOrganizations, isLoading: userOrganizationsLoading } =
    useQuery({
      queryKey: ["getUserOrganizations"],
      queryFn: () => fetchUserOrganizations(user?.["cognito:username"]),
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      onError: (error) => {
        console.error("Failed to fetch user organizations:", error);
      },
      staleTime: Infinity,
      ...queryCommonFlags,
      enabled: !!user?.["cognito:username"],
    });

  const {
    data: userOrganizationsByManagers,
    isLoading: userOrganizationsByManagersLoading,
    // isFetchingNextPage: isFetchingNextUserOrganizationsByManagers,
    // fetchNextPage: fetchNextUserOrganizationsByManagers,
    isError: userOrganizationsByManagersError,
  } = useQuery({
    queryKey: ["getUserOrganizationsByManagers", selectedManagers],
    queryFn: () =>
      fetchUserOrganizations(user?.["cognito:username"], {
        managerUIds: selectedManagers?.map((manager) => manager?.uid),
        page: 1,
        pageSize: organizationsPageSize,
      }),
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    onError: (error) => {
      console.error("Failed to fetch user organizations:", error);
    },
    staleTime: Infinity,
    ...queryCommonFlags,
    enabled: !!user?.["cognito:username"] && selectedManagers?.length > 0,
  });

  useEffect(() => {
    if (!userOrganizations) return;

    const wrappedOrganization: managedOrganization[] = userOrganizations?.data
      ?.organization
      ? [
          {
            organization: {
              ...userOrganizations.data.organization,
            },
            id: userOrganizations.data.id,
            uid: userOrganizations.data.uid,
            deletedAt: userOrganizations.data.deletedAt || null,
            createdAt: userOrganizations.data.createdAt,
            updatedAt: userOrganizations.data.updatedAt,
          },
        ]
      : [];

    const mergedOrganizations: managedOrganization[] = (
      userOrganizations?.data?.manager || []
    ).concat(wrappedOrganization);
    setAllOrganizations(mergedOrganizations);
  }, [userOrganizations]);
  useEffect(() => {
    if (selectedOrganization?.uid) {
      localStorage.setItem(`selectedOrganizationUid`, selectedOrganization.uid);
      localStorage.setItem(
        `selectedOrganizationName`,
        selectedOrganization.name,
      );
    }
  }, [selectedOrganization]);

  const updateFavicon = (href: string) => {
    const bustCache = (url: string) => `${url}?v=${Date.now()}`;

    const favicon = document.getElementById("favicon") as HTMLLinkElement;
    if (favicon) favicon.href = bustCache(href);

    let appleTouchIcon = document.getElementById("apple-touch-icon") as HTMLLinkElement;
    if (!appleTouchIcon) {
      appleTouchIcon = document.createElement("link");
      appleTouchIcon.id = "apple-touch-icon";
      appleTouchIcon.rel = "apple-touch-icon";
      document.head.appendChild(appleTouchIcon);
    }
    appleTouchIcon.href = bustCache(href);
  };

  // Set favicon immediately on mount for COMPANY_NAME-based default (before org loads)
  useEffect(() => {
    if (ORGANIZATION_TIER_TYPES.taskeen === `${EnvironmentVariables.COMPANY_NAME}`) {
      updateFavicon("/taskeen-favicon.png");
    }
  }, []);

  // Update favicon when organization data loads (custom fav or tier-based)
  useEffect(() => {
    if (selectedOrganization?.fav?.link) {
      updateFavicon(selectedOrganization.fav.link);
    } else if (
        selectedOrganization?.tier?.type === ORGANIZATION_TIER_TYPES.taskeen
    ) {
      updateFavicon("/taskeen-favicon.png");
    } else if (selectedOrganization) {
      updateFavicon("/favicon.png");
    }
  }, [
    selectedOrganization?.fav?.link,
    selectedOrganization?.tier?.type,
  ]);

  useEffect(() => {
    document.title = 
        ORGANIZATION_TIER_TYPES.taskeen === `${EnvironmentVariables.COMPANY_NAME}` && managedTaskeenOrganizations &&managedTaskeenOrganizations?.length > 0
    ? "Taskeen CRM" : `${selectedOrganization?.name || ""} CRM`
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }, [selectedOrganization?.name]);

  const managedTaskeenOrganizations = userOrganizations?.data?.manager?.filter(
    (org) => org.organization?.tier?.type === "taskeen",
  );

  const managedNawyOrganizations = userOrganizations?.data?.manager?.filter(
    (org) => org.organization?.tier?.type === ORGANIZATION_TIER_TYPES.nawy,
  );

  useEffect(() => {
    if (allOrganizations.length === 0) return;

    const filteredManagedOrganizations =
      ORGANIZATION_TIER_TYPES.taskeen === `${EnvironmentVariables.COMPANY_NAME}`
        ? allOrganizations?.filter(
            (org) =>
              org?.organization?.tier?.type ===
                ORGANIZATION_TIER_TYPES.taskeen ||
              org?.organization?.name === ORGANIZATION_TIER_TYPES.taskeen,
          )
        : ORGANIZATION_TIER_TYPES.partner ===
            `${EnvironmentVariables.COMPANY_NAME}`
          ? allOrganizations?.filter(
              (org) =>
                org?.organization?.tier?.type ===
                  ORGANIZATION_TIER_TYPES.partner ||
                org?.organization?.name === ORGANIZATION_TIER_TYPES.partner,
            )
          : allOrganizations?.filter(
              (org) =>
                org?.organization?.tier?.type ===
                  ORGANIZATION_TIER_TYPES.nawy ||
                org?.organization?.name === ORGANIZATION_TIER_TYPES.nawy,
            );

    setFilteredManagedOrganizations(filteredManagedOrganizations || []);
  }, [allOrganizations]);

  useEffect(() => {
    const storedOrgUid = localStorage.getItem(`selectedOrganizationUid`);

    const nawyOrganization =
      ORGANIZATION_TIER_TYPES.nawy === `${EnvironmentVariables.COMPANY_NAME}` &&
      managedNawyOrganizations?.length &&
      managedNawyOrganizations?.length > 0
        ? managedNawyOrganizations?.[0]?.organization
        : userOrganizations?.data?.organization;

    const initialOrg =
      ORGANIZATION_TIER_TYPES.nawy === `${EnvironmentVariables.COMPANY_NAME}`
        ? nawyOrganization
        : storedOrgUid &&
            filteredManagedOrganizations?.find(
              (org) => org?.organization?.uid === storedOrgUid,
            )?.organization
          ? filteredManagedOrganizations?.find(
              (org) => org?.organization?.uid === storedOrgUid,
            )?.organization
          : filteredManagedOrganizations?.[0]?.organization ||
            userOrganizations?.data?.organization;

    setSelectedOrganization(initialOrg);
  }, [filteredManagedOrganizations]);

  const renderOrganisationsActions =
    filteredManagedOrganizations?.length > 1 &&
    `${EnvironmentVariables.COMPANY_NAME}` !== ORGANIZATION_TIER_TYPES.nawy;

  useEffect(() => {
    if (selectedOrganizations.length === 0 && renderOrganisationsActions) {
      setShowMultipleOrganizationTables(false);
    }
  }, [
    selectedOrganizations,
    setShowMultipleOrganizationTables,
    renderOrganisationsActions,
  ]);

  return (
    <OrganizationContext.Provider
      value={{
        renderOrganisationsActions,
        selectedOrganization,
        setSelectedOrganization,
        selectedOrganizations,
        setSelectedOrganizations,
        selectedSingleOrganization,
        setSelectedSingleOrganization,
        showSingleOrganizationTables,
        setShowSingleOrganizationTables,
        showMultipleOrganizationTables,
        setShowMultipleOrganizationTables,
        selectedOrganizationCountry: selectedOrganization?.tier?.country,
        mainOrganization: userOrganizations?.data?.organization,
        managedOrganizations: userOrganizations?.data?.manager,
        filteredManagedOrganizations: filteredManagedOrganizations,
        userOrganizationsLoading: userOrganizationsLoading,
        userTiers: userOrganizations?.data?.userOrganizationsTierTypes,
        managedTaskeenOrganizations,
        accountManagers,
        accountManagersLoading: isLoading,
        accountManagersError: isError,
        userTags: userOrganizations?.data?.tags,
        // fetchNextAccountManagers: fetchNextPage,
        // isFetchingNextAccountManagers: isFetchingNextPage,
        setAccountManagersPageSize,
        setOrganizationsPageSize,
        selectedManagers,
        setSelectedManagers,
        userOrganizationsByManagers,
        userOrganizationsByManagersLoading,
        userOrganizationsByManagersError,
        setFilterType,
        filterType,
        // fetchNextUserOrganizationsByManagers:
        // fetchNextUserOrganizationsByManagers,
        // isFetchingNextUserOrganizationsByManagers:
        // isFetchingNextUserOrganizationsByManagers,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = (): OrganizationContextType => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error(
      "useOrganization must be used within an OrganizationProvider",
    );
  }
  return context;
};
